import React from 'react';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid, Typography, useMediaQuery } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { FieldError } from '@spike/model';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import AddOnService from '@spike/addon-service-model';
import { Product } from '@spike/product-model';
import { MarketplaceTax } from '@spike/marketplace-model';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { useSingleSaleTableItemStyles } from 'components/Payments/SingleSale/Table/Item/SingleSaleTableItem.styles';
import {
    GenericOrderItem,
    getOrderItemAmount,
    getOrderItemSubtotal,
    isAddOnServiceOrderItem as isAddOnServiceOrderItemType,
    isCustomOrderItem as isCustomOrderItemType,
    isProductOrderItem as isProductOrderItemType,
    updateOrderItemDiscount,
    updateOrderItemQuantity
} from 'components/Payments/SingleSale/SingleSale';
import { SingleSaleTableItemSelector } from 'components/Payments/SingleSale/Table/Item/Selector/SingleSaleTableItemSelector';
import { SingleSaleTableItemType } from 'components/Payments/SingleSale/Table/Item/Type/SingleSaleTableItemType';
import { SingleSaleTableItemQuantity } from 'components/Payments/SingleSale/Table/Item/Quantity/SingleSaleTableItemQuantity';
import { SingleSaleTableItemDiscount } from 'components/Payments/SingleSale/Table/Item/Discount/SingleSaleTableItemDiscount';
import { SingleSaleTableItemTaxes } from 'components/Payments/SingleSale/Table/Item/Taxes/SingleSaleTableItemTaxes';
import { SingleSaleTableItemSubtotal } from 'components/Payments/SingleSale/Table/Item/Subtotal/SingleSaleTableItemSubtotal';
import { Discount, OrderizableTypes } from '@spike/order-model';
import { SingleSaleTableItemPrice } from 'components/Payments/SingleSale/Table/Item/Price/SingleSaleTableItemPrice';

interface SingleSaleTableItemProps {
    item: GenericOrderItem;
    errors?: Array<FieldError>;
    selectedProductIds: Array<number>;
    selectedAddOnServiceIds: Array<number>;
    onClick?: () => void;
    onChange: (orderItem: GenericOrderItem) => void;
    onRemove: (uuid: string) => void;
}

export const SingleSaleTableItem: React.FC<SingleSaleTableItemProps> = props => {
    const classes = useSingleSaleTableItemStyles();

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

    const orderItemProduct = React.useMemo(
        () => (isProductOrderItemType(props.item) ? props.item.product : null),
        [props.item]
    );

    const orderItemAddOnService = React.useMemo(
        () => (isAddOnServiceOrderItemType(props.item) ? props.item.addOnService : null),
        [props.item]
    );

    const isCustomOrderItem = React.useMemo(() => isCustomOrderItemType(props.item), [props.item]);

    const hasError =
        props.errors && props.errors.some(error => error.fieldName === `${props.item.orderItem.uuid}_duplicated`);

    const onChangeQuantityHandler = (quantity: number) => {
        props.onChange(updateOrderItemQuantity(props.item, quantity));
    };

    const changeSelectorHandler = (
        type: OrderizableTypes,
        product?: Product,
        addOnService?: AddOnService,
        description?: string
    ) => {
        if (type === OrderizableTypes.PRODUCT) {
            const newOrderItem = { ...props.item, product };

            props.onChange({
                ...props.item,
                product,
                orderItem: {
                    ...props.item.orderItem,
                    amount: getOrderItemAmount(newOrderItem, true),
                    total: getOrderItemSubtotal(newOrderItem, true),
                    description:
                        (isEmpty(product?.businessProduct?.name) ? product?.name : product?.businessProduct?.name) || ''
                }
            });
        }

        if (type === OrderizableTypes.ADDON_SERVICE && addOnService) {
            const newOrderItem = { ...props.item, addOnService };

            props.onChange({
                ...props.item,
                addOnService,
                orderItem: {
                    ...props.item.orderItem,
                    description: addOnService.name || '',
                    amount: getOrderItemAmount(newOrderItem, true),
                    total: getOrderItemSubtotal(newOrderItem, true)
                }
            });
        }

        if (type === OrderizableTypes.CUSTOM) {
            props.onChange({
                ...props.item,
                orderItem: {
                    ...props.item.orderItem,
                    description: description || ''
                }
            });
        }
    };

    const changeSubtotalHandler = (subtotal: number | undefined) => {
        const newOrderItem = {
            ...props.item,
            orderItem: { ...props.item.orderItem, amount: subtotal ? subtotal.toString() : '0.00' }
        };

        props.onChange({
            ...newOrderItem,
            orderItem: {
                ...newOrderItem.orderItem,
                total: getOrderItemSubtotal(newOrderItem, true)
            }
        });
    };

    const changeDiscountHandler = (discount: Discount) => {
        props.onChange(updateOrderItemDiscount(props.item, discount));
    };

    const changeTaxHandler = (tax: MarketplaceTax | undefined) => {
        props.onChange({
            ...props.item,
            orderItem: {
                ...props.item.orderItem,
                taxId: tax?.id
            }
        });
    };

    return (
        <>
            {isMobile && (
                <>
                    <div className={classes.gridContainer}>
                        <div className={classes.mobileGridHeader}>
                            <div className={classes.mobileGridTwoColumns}>
                                {(orderItemProduct?.id || orderItemAddOnService?.id) && (
                                    <Typography className={classes.mobileDescription}>
                                        {props.item.orderItem.description}
                                    </Typography>
                                )}

                                <SingleSaleTableItemSelector
                                    item={props.item}
                                    selectedProductIds={props.selectedProductIds}
                                    selectedAddOnServiceIds={props.selectedAddOnServiceIds}
                                    onChange={changeSelectorHandler}
                                />

                                <SingleSaleTableItemPrice
                                    item={props.item}
                                    errors={props.errors}
                                    className={classes.mobilePrice}
                                    inputClassName={clsx(classes.field, {
                                        [classes.individualFieldPrice]:
                                            props.item.orderItem.orderizable.type === OrderizableTypes.CUSTOM
                                    })}
                                    onChange={changeSubtotalHandler}
                                />
                            </div>

                            <div className={classes.mobileGridTwoColumns}>
                                <SingleSaleTableItemType type={props.item.orderItem.orderizable.type} />

                                <SingleSaleTableItemQuantity
                                    value={props.item.orderItem.quantity}
                                    onChange={onChangeQuantityHandler}
                                />
                            </div>
                        </div>

                        <div className={classes.mobileGridTable}>
                            {!isCustomOrderItem && (
                                <div className={clsx(classes.mobileGridTwoColumns, classes.mobileGridRow)}>
                                    <Typography className={classes.mobileGridTableLabel}>Discount</Typography>

                                    <SingleSaleTableItemDiscount
                                        errors={props.errors}
                                        discount={props.item.orderItem.discount}
                                        name={`${props.item.orderItem.uuid}_discount`}
                                        onChange={changeDiscountHandler}
                                    />
                                </div>
                            )}

                            <div className={clsx(classes.mobileGridTwoColumns, classes.mobileGridRow)}>
                                <Typography className={classes.mobileGridTableLabel}>Tax</Typography>

                                <SingleSaleTableItemTaxes
                                    item={props.item}
                                    onChange={changeTaxHandler}
                                />
                            </div>
                            <div className={clsx(classes.mobileGridTwoColumns, classes.mobileGridRow)}>
                                <Typography
                                    className={classes.mobileGridTableLabel}
                                    style={{ fontWeight: '600' }}
                                >
                                    Total
                                </Typography>

                                <span style={{ fontWeight: '600' }}>
                                    <SingleSaleTableItemSubtotal
                                        item={props.item}
                                        errors={props.errors}
                                        onChange={changeSubtotalHandler}
                                    />
                                </span>
                            </div>
                        </div>

                        <div className={classes.mobileGridFooter}>
                            <button
                                type="button"
                                aria-label="Remove item"
                                className={classes.mobileDeleteButton}
                                onClick={() => props.onRemove(props.item.orderItem.uuid)}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                                Delete
                            </button>
                        </div>
                    </div>
                </>
            )}

            {!isMobile && (
                <Grid
                    container
                    key={props.item.orderItem.uuid}
                    className={clsx(classes.gridContainer, { [classes.errorBackground]: hasError })}
                >
                    <Grid
                        item
                        md={3}
                        className={classes.gridCellName}
                    >
                        {(orderItemProduct || orderItemAddOnService) && (
                            <Typography>{props.item.orderItem.description}</Typography>
                        )}

                        <SingleSaleTableItemSelector
                            item={props.item}
                            errors={props.errors}
                            selectedProductIds={props.selectedProductIds}
                            selectedAddOnServiceIds={props.selectedAddOnServiceIds}
                            onChange={changeSelectorHandler}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={1}
                        className={classes.gridCenter}
                    >
                        <SingleSaleTableItemType type={props.item.orderItem.orderizable.type} />
                    </Grid>
                    <Grid
                        item
                        xs={1}
                        className={classes.gridCenter}
                    >
                        <SingleSaleTableItemQuantity
                            value={props.item.orderItem.quantity}
                            onChange={onChangeQuantityHandler}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        className={classes.gridCenter}
                    >
                        {!isCustomOrderItem && (
                            <SingleSaleTableItemDiscount
                                discount={props.item.orderItem.discount}
                                name={`${props.item.orderItem.uuid}_discount`}
                                errors={props.errors}
                                onChange={changeDiscountHandler}
                            />
                        )}
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        className={classes.gridCenter}
                    >
                        <SingleSaleTableItemTaxes
                            item={props.item}
                            onChange={changeTaxHandler}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        className={classes.gridCenter}
                    >
                        <SingleSaleTableItemSubtotal
                            item={props.item}
                            errors={props.errors}
                            onChange={changeSubtotalHandler}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={1}
                        className={classes.gridCenter}
                    >
                        <div>
                            <FontAwesomeIcon
                                icon={faTimes}
                                className={classes.closeIcon}
                                onClick={() => props.onRemove(props.item.orderItem.uuid)}
                            />
                        </div>
                    </Grid>
                </Grid>
            )}
        </>
    );
};
